import { Component, type ErrorInfo, type ReactNode } from 'react'
import { RiErrorWarningLine } from '@remixicon/react'
import { Card } from 'components/ui/card'
import { Button } from 'components/ui/Button'
import { ApiError } from 'lib/ApiError'
import { reportError } from 'lib/rum'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  correlationId: string | null
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, correlationId: null, error: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const correlationId = (error as ApiError).correlationId ?? undefined
    reportError(error, {
      componentStack: info.componentStack ?? '',
      correlationId,
    })
    this.setState({
      correlationId: correlationId ?? null,
    })
  }

  render(): ReactNode {
    const { hasError, correlationId } = this.state
    const { children, fallback } = this.props

    if (!hasError) return children

    if (fallback) return fallback

    return (
      <div
        data-testid='error-boundary-fallback'
        className='flex min-h-screen items-center justify-center p-6'
      >
        <Card className='w-full max-w-md border-red-200 bg-red-50 p-8 text-center dark:border-red-900/50 dark:bg-red-950/30'>
          <RiErrorWarningLine
            size='48px'
            className='mx-auto mb-4 text-red-500'
          />
          <h2 className='mb-2 text-xl font-semibold text-red-600 dark:text-red-400'>
            Something went wrong
          </h2>
          <p className='mb-6 text-sm text-red-600/80 dark:text-red-400/80'>
            An unexpected error occurred. Please try returning to the home page.
          </p>
          {correlationId && (
            <div className='mb-6 rounded border border-red-200 bg-red-100 p-3 dark:border-red-900/50 dark:bg-red-950/50'>
              <p className='mb-2 text-xs text-red-600/70 dark:text-red-400/70'>
                Error reference
              </p>
              <p className='break-all font-mono text-sm text-red-700 dark:text-red-300'>
                {correlationId}
              </p>
              <Button
                className='mt-2 text-xs'
                onClick={() => navigator.clipboard.writeText(correlationId)}
                aria-label='Copy error reference'
              >
                Copy
              </Button>
            </div>
          )}
          <Button onClick={() => (window.location.href = '/')}>Go Home</Button>
        </Card>
      </div>
    )
  }
}
